<script setup lang="ts">
const { t: $t } = useI18n()

const { openLogisticsTaskInformationPopup: { data: popupData, close: closePopup } } = useGlobalOpeners()

const id = computed(() => popupData.value?.id)

const { logisticsTask: queryLogisticsTask } = useQuery()
const { data: logisticsTask, isLoading: isLoadingLogisticsTask } = queryLogisticsTask.byIdOrThrow(id)

const { openOfferPage } = useGlobalOpeners()

const router = useRouter()

function handleOpenOfferPage() {
  if (!logisticsTask.value?.offer) {
    return
  }

  openOfferPage({ mode: 'edit', id: logisticsTask.value.offer.id, redirectUrl: router.currentRoute.value.fullPath }).then(() => closePopup())
}

function navigateToDeliveryNotePDF() {
  if (!logisticsTask.value) {
    return
  }

  if (logisticsTask.value.type === 'defect') {
    const customerLocale = logisticsTask.value.customer?.language || 'de'
    window.open(`/api/pdf/logisticsTask/defectDelivery/${logisticsTask.value.id}?locale=${customerLocale}`, '_blank')
    return
  }

  if (!logisticsTask.value.offer) {
    return
  }
  if (logisticsTask.value.offer.type === 'service-project' && logisticsTask.value.type === 'aToB') {
    window.open(`/api/pdf/logisticsTask/delivery/${logisticsTask.value.id}`, '_blank')
    return
  }

  const customerLocale = logisticsTask.value.offer.customer?.language || 'de'
  window.open(`/api/pdf/offer/details/${logisticsTask.value.offer.cuid}?type=deliveryNote&locale=${customerLocale}`, '_blank')
}

const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()

const setProcessedExternally = useMutation({
  mutationFn: $trpc.logisticsTask.setProcessedExternally.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('query.error', { entity: $t('logisticsTask.name') }) }),
  onSuccess: async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['logisticsTask'] }),
      queryClient.invalidateQueries({ queryKey: ['offer'] }),
    ])

    popupConfirmProcessedExternallyOpen.value = false
    notification.success({ title: $t('query.success', { entity: $t('logisticsTask.name') }), duration: 4500 })
  },
})

const isRentalOffer = computed(() => logisticsTask.value?.offer?.type === 'rental')

const showSetProcessedExternallyButton = computed(() => {
  if (!logisticsTask.value || !isRentalOffer.value || logisticsTask.value.isProcessedExternally || !['inbound', 'outbound'].includes(logisticsTask.value.type)) {
    return false
  }

  return logisticsTask.value.positionsToShip.some((position) => {
    if (position.type === 'machinery' && position.machinery) {
      return position.machinery.isForeignMachinery
    }
    if (position.type === 'machineryAccessory' && position.machineryAccessory) {
      return position.machineryAccessory.isForeignAccessory
    }
    return false
  })
})

const popupConfirmProcessedExternallyOpen = ref(false)

const showSetRevertProcessedExternallyButton = computed(() => {
  if (!logisticsTask.value || !isRentalOffer.value || !logisticsTask.value.isProcessedExternally || !['inbound', 'outbound'].includes(logisticsTask.value.type)) {
    return false
  }

  return logisticsTask.value.positionsToShip.some((position) => {
    if (position.type === 'machinery' && position.machinery) {
      return position.machinery.isForeignMachinery
    }
    if (position.type === 'machineryAccessory' && position.machineryAccessory) {
      return position.machineryAccessory.isForeignAccessory
    }
    return false
  })
})

const disableRevertConfirmProcessedExternallyButton = computed(() => {
  if (logisticsTask.value?.type === 'outbound' && showSetRevertProcessedExternallyButton.value) {
    return logisticsTask.value.positionsToShip.some(({ terminatedAt }) => !!terminatedAt)
  }
  return false
})

const title = computed(() => {
  if (!logisticsTask.value) {
    return ''
  }
  if (logisticsTask.value.type === 'defect') {
    return `${$t('logisticsTask.type.defect')} (${logisticsTask.value.defect?.machineryId ?? logisticsTask.value.defect?.machineryAccessoryId})`
  }

  return `${$t(`logisticsTask.type.${logisticsTask.value.type}`)} (${logisticsTask.value.offer?.id})`
})
</script>

<template>
  <ThePopup v-if="logisticsTask && popupData" :show="Boolean(logisticsTask)" :title="title" width="870px" @close="closePopup">
    <TheConfirmPopup
      v-if="popupConfirmProcessedExternallyOpen"
      :action-button-label="$t('button.label.proceed')"
      @confirm="setProcessedExternally.mutate({ ids: [{ id: logisticsTask.id }], isRevert: false })"
      @close="popupConfirmProcessedExternallyOpen = false"
    >
      {{ $t('logisticsTask.details.confirmProcessedExternally') }}
    </TheConfirmPopup>

    <div v-if="['inbound', 'outbound', 'aToB'].includes(logisticsTask.type) && logisticsTask.offer">
      <div class="my-4 flex gap-4">
        <n-button @click="handleOpenOfferPage">
          Zum Auftrag
        </n-button>
        <n-button
          :disabled="!logisticsTask.deliveryAt"
          :title="logisticsTask.deliveryAt ? undefined : 'Lieferung ist noch nicht geplant'"
          @click="navigateTo(`/logistics-planning?dispatcherSelectedTime=${logisticsTask.deliveryAt?.getTime()}`)"
        >
          Zum Dispo-Planer
        </n-button>
        <n-button @click="navigateToDeliveryNotePDF">
          {{ $t('button.printDeliverySlip') }}
        </n-button>
        <n-button
          v-if="showSetProcessedExternallyButton"
          type="primary"
          secondary
          @click="popupConfirmProcessedExternallyOpen = true"
        >
          {{ $t('logisticsTask.details.setProcessedExternally') }}
        </n-button>
        <n-button
          v-if="showSetRevertProcessedExternallyButton"
          type="primary"
          secondary
          :disabled="disableRevertConfirmProcessedExternallyButton"
          @click="setProcessedExternally.mutate({ ids: [{ id: logisticsTask.id }], isRevert: true })"
        >
          {{ $t('logisticsTask.details.revertProcessedExternally') }}
        </n-button>
      </div>

      <n-alert v-if="logisticsTask.isProcessedExternally" type="warning" class="mb-2">
        {{ $t('logisticsTask.details.tooltip.isProcessedExternally') }}
      </n-alert>

      <LogisticsInformationCard :is-loading="isLoadingLogisticsTask" :logistics-task="logisticsTask" />
    </div>
    <div v-else-if="logisticsTask.type === 'defect'" class="space-y-2">
      <n-button @click="navigateToDeliveryNotePDF">
        {{ $t('button.printDeliverySlip') }}
      </n-button>
      <LogisticsInformationCard :is-loading="isLoadingLogisticsTask" :logistics-task="logisticsTask" />
    </div>
    <div v-else>
      Zu dieser Aufgabe können keine Informationen angezeigt werden ({{ logisticsTask.type }}, {{ logisticsTask.id }})
    </div>
  </ThePopup>
</template>
